import React from "react";
import { Link } from "react-router-dom";
import SectionHeading from "./SectionHeading";
import { LANDING } from "@/constants/testIds";
import { LEADERSHIP } from "@/data/leadership";
import { REGIONAL_COVERAGE } from "@/data/regionalCoverage";

/**
 * The Partnership — homepage block.
 *
 * Three-tier senior structure:
 *  · Founding Partners: Marcel Kellermann, Chris Reid
 *  · Partners: Rachel Yang (Asia-Pacific), Ryan Landry (Strategic Capital)
 *  · Leadership: Executive layer below the partnership — senior function
 *    heads without partner economics (Head of Capital Formation, etc.)
 *
 * Followed by the structural pillar grid.
 */
const FOUNDING_PARTNERS = [
  {
    id: "mk",
    anchor: "marcel-kellermann",
    initials: "MK",
    name: "Marcel Kellermann",
    role: "Managing Partner",
    focus:
      "Investor Relations · Capital Formation · Investment Strategy",
    short:
      "Marcel leads investor relations, capital formation, investment strategy, and institutional partnership development for Kellermann Reid & Co.",
    email: "marcel.kellermann@krprivatecapital.com",
    linkedin: "https://www.linkedin.com/in/marcel-kellermann/",
    photo: "/portraits/marcel-kellermann.jpg",
  },
  {
    id: "cr",
    anchor: "chris-reid",
    initials: "CR",
    name: "Chris Reid",
    role: "Managing Partner",
    focus: "Commercial Finance · Strategy · Capital Partnerships",
    short:
      "Chris supports the firm's commercial finance strategy, capital partnerships, operational execution, and platform development.",
    email: "chris.reid@krprivatecapital.com",
    linkedin: "https://www.linkedin.com/in/chris-reid-847a0133/",
    photo: "/portraits/chris-reid.jpg",
  },
];

const PARTNERS = [
  {
    id: "ry",
    anchor: "rachel-yang",
    initials: "RY",
    name: "Rachel Yang",
    role: "Partner · Asia-Pacific",
    focus: "Deal Origination · Investor Relations · Asia-Pacific Coverage",
    short:
      "Rachel anchors Asia-Pacific origination and investor coverage, with deep expertise across commercial and high-value asset finance from major banking institutions.",
    email: "rachel.yang@krprivatecapital.com",
    linkedin: "https://www.linkedin.com/in/rachel-yang-44a91297/",
    photo: "/portraits/rachel-yang.jpg",
  },
  {
    id: "rl",
    anchor: "ryan-landry",
    initials: "RL",
    name: "Ryan Landry",
    role: "Partner · Strategic Capital",
    focus: "Strategic Capital · Family Offices · Investor Partnerships",
    short:
      "Ryan leads the platform's engagement with family offices and global investment partners. An award-winning operations executive with extensive experience across M&A, capital fundraising, leadership transformation and strategic acceleration.",
    email: "ryan.landry@krprivatecapital.com",
    linkedin: "https://www.linkedin.com/in/ryanmlandry/",
    photo: "/portraits/ryan-landry.jpg",
  },
];

const PILLARS = [
  {
    role: "Office of the Senior Partner",
    body:
      "Sets strategy, capital allocation policy and the standards by which capital is committed. Sponsors every transaction through the investment committee.",
    seal: "I",
  },
  {
    role: "Investment Committee",
    body:
      "Voting body comprising senior investment principals and independent perspectives. Documented process, written deliberation, recorded dissent.",
    seal: "II",
  },
  {
    role: "Origination & Underwriting",
    body:
      "Sector-aligned principals sourcing through proprietary operating, advisory and lending relationships. Underwriting led by tangible-asset coverage and cash-flow durability.",
    seal: "III",
  },
  {
    role: "Operating Partner Network",
    body:
      "Senior industrial operators engaged across diligence, governance and value creation. Aligned via long-term incentives and explicit accountability.",
    seal: "IV",
  },
  {
    role: "Risk, Legal & Compliance",
    body:
      "Independent oversight across investment, market, operational, regulatory and conflicts risk. Reporting line outside the deal teams.",
    seal: "V",
  },
  {
    role: "Limited Partner Relations",
    body:
      "Senior dialogue with strategic capital partners, placement agents, family offices and institutional counterparties. Confidential, by introduction.",
    seal: "VI",
  },
];

const FounderCard = ({ founder, profileBase = "/partnership" }) => {
  const firstName = founder.name.split(" ")[0];
  return (
    <article
      id={founder.anchor}
      data-testid={`${LANDING.partnershipFounderPrefix}${founder.id}`}
      className="relative p-8 lg:p-10 border border-[var(--kr-line)] hover:border-[var(--kr-gold)]/60 transition-colors duration-500 group scroll-mt-32"
      style={{ background: "rgba(12, 16, 24, 0.6)" }}
    >
      <div className="flex gap-6 items-start mb-6">
        {/* portrait */}
        <div className="relative w-24 h-28 flex-shrink-0 overflow-hidden border border-[var(--kr-line)] bg-[#0c1018]">
          {founder.photo ? (
            <>
              <img
                src={founder.photo}
                alt={founder.name}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  filter:
                    "grayscale(35%) contrast(1.05) brightness(0.95) saturate(0.9)",
                  objectPosition: "center top",
                }}
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(10,13,18,0) 55%, rgba(10,13,18,0.55) 100%)",
                }}
              />
            </>
          ) : (
            <>
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse at 35% 25%, rgba(201,169,110,0.22), transparent 55%), linear-gradient(180deg, #11161f 0%, #0a0d12 100%)",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center font-serif-display italic text-[2rem] text-[var(--kr-gold)]">
                {founder.initials}
              </div>
            </>
          )}
          <span className="absolute top-1 left-1 w-3 h-3 border-l border-t border-[var(--kr-gold)]/70" />
          <span className="absolute top-1 right-1 w-3 h-3 border-r border-t border-[var(--kr-gold)]/70" />
          <span className="absolute bottom-1 left-1 w-3 h-3 border-l border-b border-[var(--kr-gold)]/70" />
          <span className="absolute bottom-1 right-1 w-3 h-3 border-r border-b border-[var(--kr-gold)]/70" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-mono-label text-[9px] text-[var(--kr-gold)] mb-2">
            {founder.role}
          </div>
          <h3 className="font-serif-display text-[var(--kr-text)] text-[1.6rem] leading-[1.1] mb-2">
            {founder.name}
          </h3>
          <div className="font-serif-display italic text-[var(--kr-text-muted)] text-[12.5px] leading-[1.45]">
            {founder.focus}
          </div>
        </div>
      </div>

      <div className="kr-rule mb-6" />

      <p className="text-[var(--kr-text)] text-[14.5px] leading-[1.75] mb-6 min-h-[5em]">
        {founder.short}
      </p>

      {/* email line */}
      <a
        href={`mailto:${founder.email}`}
        data-testid={`${LANDING.partnerEmailPrefix}${founder.id}`}
        className="kr-link font-serif-display text-[0.95rem] text-[var(--kr-text)] break-all block mb-6"
      >
        {founder.email}
      </a>

      <div className="flex flex-wrap gap-3">
        <a
          href={`mailto:${founder.email}`}
          data-testid={`${LANDING.partnerContactBtnPrefix}${founder.id}`}
          className="kr-btn-primary"
          style={{ padding: "0.85rem 1.5rem", fontSize: "10px" }}
        >
          Contact {firstName}
          <span aria-hidden>→</span>
        </a>
        {founder.linkedin ? (
          <a
            href={founder.linkedin}
            target="_blank"
            rel="noreferrer"
            data-testid={`${LANDING.partnerLinkedinBtnPrefix}${founder.id}`}
            className="kr-btn-ghost"
            style={{ padding: "0.85rem 1.5rem", fontSize: "10px" }}
          >
            LinkedIn ↗
          </a>
        ) : (
          <span
            aria-disabled="true"
            data-testid={`${LANDING.partnerLinkedinBtnPrefix}${founder.id}`}
            className="kr-btn-ghost opacity-50 cursor-not-allowed"
            title="LinkedIn forthcoming"
            style={{ padding: "0.85rem 1.5rem", fontSize: "10px" }}
          >
            LinkedIn · Forthcoming
          </span>
        )}
        <Link
          to={`${profileBase}#${founder.anchor}`}
          data-testid={`${LANDING.partnerProfileBtnPrefix}${founder.id}`}
          className="font-mono-label text-[10px] text-[var(--kr-text-muted)] hover:text-[var(--kr-gold)] transition-colors self-center pl-1"
        >
          Full Profile →
        </Link>
      </div>
    </article>
  );
};

const Partnership = () => {
  return (
    <section
      id="partnership"
      data-testid={LANDING.partnership}
      className="relative py-32 lg:py-44 border-t border-[var(--kr-line-soft)]"
      style={{ background: "var(--kr-bg-2)" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 mb-16">
          <div className="lg:col-span-7">
            <SectionHeading
              index="11"
              label="The Partnership"
              title={
                <>
                  Founded by{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    Marcel Kellermann
                  </span>{" "}
                  &amp;{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    Chris Reid
                  </span>
                  . Asia-Pacific anchored by{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    Rachel Yang
                  </span>
                  .
                </>
              }
            />
          </div>
          <div className="lg:col-span-5">
            <p className="text-[var(--kr-text-muted)] text-[16px] leading-[1.85] lg:pt-10">
              Kellermann Reid &amp; Co. is organised as a senior partnership.
              Investment decisions are made by committee, documented in writing
              and informed by an operating partner network with industrial
              depth. Senior dialogue with limited partners sits outside the
              deal teams.
            </p>
          </div>
        </div>

        {/* Senior partnership — two tiers */}
        {/* Tier 1 — Founding Partners */}
        <div className="flex items-center gap-4 mb-6">
          <span
            aria-hidden
            className="block w-[8px] h-[8px]"
            style={{
              background: "var(--kr-gold)",
              transform: "rotate(45deg)",
            }}
          />
          <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
            Founding Partners
          </span>
          <span className="flex-1 h-px bg-[var(--kr-line)]" />
        </div>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {FOUNDING_PARTNERS.map((f) => (
            <FounderCard key={f.id} founder={f} />
          ))}
        </div>

        {/* Tier 2 — Partners */}
        <div className="flex items-center gap-4 mb-6">
          <span
            aria-hidden
            className="block w-[8px] h-[8px]"
            style={{
              background: "var(--kr-gold)",
              transform: "rotate(45deg)",
            }}
          />
          <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
            Partners
          </span>
          <span className="flex-1 h-px bg-[var(--kr-line)]" />
        </div>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {PARTNERS.map((f) => (
            <FounderCard key={f.id} founder={f} />
          ))}
        </div>

        {/* Tier 3 — Leadership (executive tier, below Partners) */}
        <div className="flex items-center gap-4 mb-6">
          <span
            aria-hidden
            className="block w-[8px] h-[8px]"
            style={{
              background: "var(--kr-gold)",
              transform: "rotate(45deg)",
            }}
          />
          <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
            Leadership
          </span>
          <span className="flex-1 h-px bg-[var(--kr-line)]" />
        </div>
        <div
          className={`grid gap-6 mb-8 ${
            LEADERSHIP.length === 1 ? "grid-cols-1" : "md:grid-cols-2"
          }`}
        >
          {LEADERSHIP.map((m) => (
            <FounderCard
              key={m.id}
              founder={m}
              profileBase="/leadership"
            />
          ))}
        </div>

        {/* Tier 4 — Regional Coverage (on-the-ground representation, below Leadership) */}
        <div className="flex items-center gap-4 mb-6">
          <span
            aria-hidden
            className="block w-[8px] h-[8px]"
            style={{
              background: "var(--kr-gold)",
              transform: "rotate(45deg)",
            }}
          />
          <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
            Regional Coverage
          </span>
          <span className="flex-1 h-px bg-[var(--kr-line)]" />
        </div>
        <div
          data-testid="kr-partnership-regional-coverage"
          className={`grid gap-6 mb-8 ${
            REGIONAL_COVERAGE.length === 1 ? "grid-cols-1" : "md:grid-cols-2"
          }`}
        >
          {REGIONAL_COVERAGE.map((m) => (
            <FounderCard
              key={m.id}
              founder={m}
              profileBase="/regional-coverage"
            />
          ))}
        </div>

        <div className="mb-16 flex items-center justify-end">
          <Link
            to="/partnership"
            data-testid={LANDING.partnershipMeetPartnership}
            className="kr-link font-mono-label text-[11px]"
          >
            Meet the Partnership →
          </Link>
        </div>

        {/* Structural pillars */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[var(--kr-line)]">
          {PILLARS.map((p) => (
            <div
              key={p.role}
              data-testid={`${LANDING.partnershipCardPrefix}${p.seal}`}
              className="p-10 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.03)] transition-colors duration-500"
            >
              <div className="flex items-baseline justify-between mb-10">
                <span className="font-serif-display italic text-[var(--kr-gold)] text-[1.4rem]">
                  {p.seal}.
                </span>
                <span
                  aria-hidden
                  className="block w-10 h-px bg-[var(--kr-line)] group-hover:w-16 group-hover:bg-[var(--kr-gold)] transition-all duration-500"
                />
              </div>
              <h3 className="font-serif-display text-[1.45rem] leading-[1.25] text-[var(--kr-text)] mb-4">
                {p.role}
              </h3>
              <p className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.75]">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Partnership;
